import React from "react";

/**
 * FoundingBadge — surfaces "Inner Circle" status anywhere a user-tier label
 * needs to display, using the TurfPulse Inner Circle crest as the visual mark.
 *
 * Renders nothing if the user isn't on the founding tier (so this can be
 * dropped into any existing tier-display block without a parent guard).
 *
 * No numbering — every Inner Circle member is equal, regardless of join order.
 *
 * Two sizes:
 *   - default: full crest + label + countdown to season end
 *   - compact: small crest + label, for tight nav contexts
 */
const SEASON_END_ISO = "2026-10-31T23:59:59Z";
const CREST_SRC = "/brand/turfpulse-inner-circle.svg";

function daysUntilSeasonEnd() {
  const now = new Date();
  const end = new Date(SEASON_END_ISO);
  const ms = end - now;
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export const FoundingBadge = ({ user, compact = false, className = "" }) => {
  if (!user) return null;
  const tier = user.subscription_tier || user.tier;
  if (tier !== "founding") return null;

  const days = daysUntilSeasonEnd();

  if (compact) {
    return (
      <span
        data-testid="founding-badge-compact"
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-emerald-50 border border-emerald-200 text-emerald-900 ${className}`}
        title="TurfPulse Inner Circle"
      >
        <img
          src={CREST_SRC}
          alt=""
          aria-hidden="true"
          className="h-4 w-4"
        />
        <span>Inner Circle</span>
      </span>
    );
  }

  return (
    <div
      data-testid="founding-badge"
      className={`inline-flex items-center gap-4 px-5 py-3.5 rounded-xl bg-gradient-to-br from-emerald-50 to-stone-50 border border-emerald-200 shadow-sm ${className}`}
    >
      <img
        src={CREST_SRC}
        alt="TurfPulse Inner Circle crest"
        className="h-14 w-14 flex-shrink-0"
      />
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-700">
          TurfPulse · Inner Circle
        </span>
        <span className="text-base font-semibold text-zinc-900 mt-0.5">
          Full access through Oct 31
        </span>
        {days > 0 && (
          <span className="text-[11px] text-zinc-500 mt-1">
            {days} {days === 1 ? "day" : "days"} of free season remaining
          </span>
        )}
      </div>
    </div>
  );
};

export default FoundingBadge;
